import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { formatEastern } from "@/lib/utils";

type AddonLine = { name: string; option_label: string | null; quantity: number; unit_price: number };
type MyBooking = {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  payment_status: string;
  total: number;
  studio_name: string | null;
  addons: AddonLine[];
};

const AccountPage = () => {
  const { user, loading, isAdmin, signOut } = useAuth();
  const [bookings, setBookings] = useState<MyBooking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("id,start_at,end_at,status,payment_status,total,studios(name)")
        .order("start_at", { ascending: false });
      const list = (data ?? []) as any[];
      const ids = list.map((b) => b.id);
      const addonMap = new Map<string, AddonLine[]>();
      if (ids.length) {
        const { data: al } = await supabase
          .from("booking_addons")
          .select("booking_id,quantity,unit_price,option_label,addons(name)")
          .in("booking_id", ids);
        (al ?? []).forEach((a: any) => {
          const arr = addonMap.get(a.booking_id) ?? [];
          arr.push({
            name: a.addons?.name ?? "Add-on",
            option_label: a.option_label ?? null,
            quantity: Number(a.quantity || 1),
            unit_price: Number(a.unit_price || 0),
          });
          addonMap.set(a.booking_id, arr);
        });
      }
      if (cancelled) return;
      setBookings(list.map((b) => ({
        id: b.id,
        start_at: b.start_at,
        end_at: b.end_at,
        status: b.status,
        payment_status: b.payment_status,
        total: Number(b.total || 0),
        studio_name: b.studios?.name ?? null,
        addons: addonMap.get(b.id) ?? [],
      })));
      setBookingsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  return (
    <div className="pt-24 min-h-screen">
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-4xl font-display tracking-widest mb-2">MY ACCOUNT</h1>
          <p className="text-muted-foreground font-body text-sm mb-10">{user.email}</p>

          <div className="grid gap-4">
            <div className="bg-panel border border-border p-6">
              <h2 className="font-display tracking-widest mb-2">PROFILE</h2>
              <p className="text-muted-foreground font-body text-sm">
                {user.user_metadata?.full_name || "—"}
              </p>
            </div>

            <div className="bg-panel border border-border p-6">
              <h2 className="font-display tracking-widest mb-2">BOOKINGS</h2>
              {bookingsLoading ? (
                <p className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Loading…</p>
              ) : bookings.length === 0 ? (
                <p className="text-muted-foreground font-body text-sm">No bookings yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {bookings.map((b) => (
                    <li key={b.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-body text-sm">
                          {b.studio_name ?? "Studio"} · {formatEastern(b.start_at)}
                        </span>
                        <span className="font-display text-primary text-sm">${b.total.toFixed(2)}</span>
                      </div>
                      <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                        {b.status} · {b.payment_status}
                      </p>
                      {b.addons.length > 0 && (
                        <div className="mt-2 space-y-0.5">
                          {b.addons.map((a, i) => (
                            <p key={i} className="text-xs font-mono text-muted-foreground">
                              <span className="text-foreground">{a.name}</span>
                              {a.option_label && <span className="text-primary"> — {a.option_label}</span>}
                              {" "}×{a.quantity} · ${(a.unit_price * a.quantity).toFixed(2)}
                            </p>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-panel border border-border p-6">
              <h2 className="font-display tracking-widest mb-2">ORDERS</h2>
              <p className="text-muted-foreground font-body text-sm">No orders yet.</p>
            </div>

            <div className="flex flex-wrap gap-3 pt-4">
              {isAdmin && (
                <Link to="/admin">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold">
                    Open Admin
                  </Button>
                </Link>
              )}
              <Button variant="outline" onClick={signOut} className="font-body">
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { AccountPage };
export default AccountPage;
